import type { QueryClient } from '@tanstack/react-query';
import { createComment, createTask, updateTask } from '@/lib/tasks';
import { addProjectMembers, createProject } from '@/lib/projects';
import { createTimeEntry } from '@/lib/time-entries';
import { viewerTimeZone } from '@/lib/timezone';
import { createEmployee } from '@/lib/employees';
import { createAbsenceRequest } from '@/lib/absences';
import { addWidget } from './widget-store';
import type { Proposal } from '@/lib/assistant';

/**
 * Applies a proposal the assistant prepared.
 *
 * Every branch calls the same REST endpoint the equivalent manual action uses, so
 * an AI-applied change goes through identical permission checks, validation and
 * activity logging — the assistant gets no privileged write path of its own.
 */
/** Where the applied change can be seen, when there is somewhere to go. */
export interface ApplyResult {
  link?: {
    label: string;
    href: string;
    /** Query params, for destinations addressed by search rather than by path. */
    search?: Record<string, string>;
  };
}

export async function applyProposal(proposal: Proposal, qc: QueryClient): Promise<ApplyResult> {
  const p = proposal.payload as Record<string, string | undefined>;
  let result: ApplyResult = {};

  switch (proposal.kind) {
    case 'create_task': {
      if (!p.boardId || !p.title) throw new Error('Incomplete proposal: board or title missing');
      const created = await createTask(p.boardId, {
        title: p.title,
        description: p.description,
        assigneeId: p.assigneeId,
        priority: p.priority,
        type: p.type,
        dueDate: p.dueDate,
      });
      // "Created" is not the end of the errand — you almost always want to look
      // at the thing, so hand back the way to it rather than only saying it
      // happened. The board opens with the task's detail panel already on.
      result = {
        link: {
          label: `Open ${created.key}`,
          href: '/boards',
          search: { board: p.boardId, task: created.key },
        },
      };
      break;
    }
    case 'assign_task': {
      if (!p.taskKey || !p.assigneeId) throw new Error('Incomplete proposal: task or assignee missing');
      await updateTask(p.taskKey, { assigneeId: p.assigneeId });
      break;
    }
    case 'set_due_date': {
      if (!p.taskKey) throw new Error('Incomplete proposal: task missing');
      // `dueDate` is deliberately allowed to be null — that clears the date.
      await updateTask(p.taskKey, { dueDate: p.dueDate ?? null });
      break;
    }
    case 'add_comment': {
      if (!p.taskKey || !p.body) throw new Error('Incomplete proposal: task or body missing');
      await createComment(p.taskKey, { body: p.body });
      break;
    }
    case 'add_project_member': {
      if (!p.projectId || !p.employeeId) throw new Error('Incomplete proposal: project or person missing');
      await addProjectMembers(p.projectId, [p.employeeId]);
      break;
    }
    case 'create_employee': {
      if (!p.firstName || !p.lastName || !p.workEmail || !p.startDate) {
        throw new Error('Incomplete proposal: name, work email or start date missing');
      }
      const created = await createEmployee({
        firstName: p.firstName,
        lastName: p.lastName,
        workEmail: p.workEmail,
        startDate: p.startDate,
        positionId: p.positionId,
        departmentId: p.departmentId,
        managerId: p.managerId,
        employmentTypeId: p.employmentTypeId,
      });
      // The point of creating someone is to go and fill in the rest of their
      // profile, so hand back the way there instead of just saying "done".
      result = {
        link: { label: `Open ${p.firstName} ${p.lastName}`, href: `/employees/${created.id}` },
      };
      break;
    }
    case 'request_absence': {
      if (!p.employeeId || !p.absenceTypeId || !p.startDate || !p.endDate || !p.totalDays) {
        throw new Error('Incomplete proposal: the absence request is missing dates or a type');
      }
      await createAbsenceRequest({
        employeeId: p.employeeId,
        absenceTypeId: p.absenceTypeId,
        startDate: p.startDate,
        endDate: p.endDate,
        // Carried as a string through the generic payload; the endpoint wants a
        // number, and 0.5-day requests are a real case, so this is not an int.
        totalDays: Number(p.totalDays),
        notes: p.notes,
      });
      // Filed, not granted — it is pending until an approver acts, and the page
      // that shows that is where the person should land.
      result = { link: { label: 'Open Absences', href: '/absences' } };
      break;
    }
    case 'log_time': {
      if (!p.projectId || !p.startedAt || !p.duration) {
        throw new Error('Incomplete proposal: the entry is missing a project or a duration');
      }
      await createTimeEntry({
        projectId: p.projectId,
        startedAt: p.startedAt,
        endedAt: p.endedAt,
        // Seconds, carried as a string through the generic payload.
        duration: Number(p.duration),
        description: p.description,
        // Not 'timer': nobody watched a clock run. It was typed or spoken after
        // the fact, which is what 'manual' means on the Time Tracker.
        source: 'manual',
        // Whose wall clock `startedAt`'s digits are: the viewer's, the same
        // zone Log Time sends. [HW-916]
        timezone: viewerTimeZone(),
      });
      result = { link: { label: 'Open Time Tracker', href: '/time' } };
      break;
    }
    case 'create_project': {
      if (!p.name || !p.slug) throw new Error('Incomplete proposal: the project has no name or key');
      const raw = proposal.payload as { managerIds?: unknown };
      const managerIds = (Array.isArray(raw.managerIds) ? raw.managerIds : []) as string[];
      if (managerIds.length === 0) {
        throw new Error('Incomplete proposal: a project needs somebody to run it');
      }
      const created = await createProject({
        name: p.name,
        slug: p.slug,
        description: p.description,
        managerIds,
        status: p.status,
      });
      result = { link: { label: 'Open project', href: `/projects/${created.id}` } };
      break;
    }
    case 'create_widget': {
      // One proposal can carry a whole dashboard, so the payload is always a
      // list — a single widget is a list of one. Approving once is the point:
      // five separate confirmations for one "build me a dashboard" is five
      // chances to lose the thread.
      // Read off the raw payload: `p` above narrows every field to a string,
      // and this is the one branch whose payload carries a list.
      const raw = (proposal.payload as { widgets?: unknown }).widgets;
      const widgets = (Array.isArray(raw) ? raw : []) as { prompt?: string; title?: string }[];
      if (widgets.length === 0 || widgets.some((w) => !w?.prompt)) {
        throw new Error('Incomplete proposal: the widget has no question to ask');
      }
      // A widget is the caller's own view preference, so it is stored on the
      // client rather than pushed through a company-data endpoint. The question
      // is what gets saved — it re-runs on each visit, so the widget shows
      // today's numbers rather than the ones it was created with.
      for (const w of widgets) addWidget(w.prompt!, w.title || w.prompt!);
      window.dispatchEvent(new CustomEvent('hygge:widgets-changed'));
      break;
    }
    default: {
      // Exhaustiveness guard: a new proposal kind added on the backend without a
      // matching branch here fails loudly instead of silently doing nothing.
      const never: never = proposal.kind;
      throw new Error(`Unsupported proposal kind: ${String(never)}`);
    }
  }

  // The change can surface in lists, boards, sprints and detail panes at once.
  await Promise.all([
    qc.invalidateQueries({ queryKey: ['tasks'] }),
    qc.invalidateQueries({ queryKey: ['sprints'] }),
    qc.invalidateQueries({ queryKey: ['backlog'] }),
    qc.invalidateQueries({ queryKey: ['board-overview'] }),
    qc.invalidateQueries({ queryKey: ['all-issues'] }),
    qc.invalidateQueries({ queryKey: ['projects'] }),
    qc.invalidateQueries({ queryKey: ['employees'] }),
    qc.invalidateQueries({ queryKey: ['time-entries'] }),
  ]);

  return result;
}
